import { Button, Col, Form, Modal, Row, Spinner, ProgressBar } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { ChangeEvent, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_DOCUMENTS_POLICY } from '../../../gql/queries/policiesApiQueries'
import { IResAllTagsDocuments } from '../../../types/policiesApi'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { useUploadFile } from '../../../hooks/useUploadFile'
import { CREATE_RESOURCE_CENTER_STR } from '../../../gql/mutations/resourceCenterMutations'
import { parseId } from '../../../helpers'
import { ICreateResourceCenterResponse } from '../../../types/resourceCenter'
import { IResourceCategoryTreeResponse } from '../../../types/resourceCategory'
import { GET_RESOURCE_CATEGORY_TREE } from '../../../gql/queries/resourceCategoryQuery'
import { flattenCategoryTree } from '../categoryTreeHelpers'
import { useDisclourse } from '../../../hooks/useDisclourse'
import { ModalManageTags } from './ModalManageTags'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import {
  BACKEND_ERROR,
  KB_UNIT,
  REQUIRED_MARK,
  RESOURCE_MESSAGES as R_,
} from '../resourceCenterMessages'
import { resourceTypeOptions } from '../resourceTypeHelpers'

/** Fabrica: los mensajes necesitan `intl`. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
  name: Yup.string().required(intl.formatMessage(R_.vNameRequired)),
  resourceType: Yup.string().required(intl.formatMessage(R_.vTypeRequired)),
  file: Yup.mixed().when('resourceMode', {
    is: 'file',
    then: (schema) => schema.required(intl.formatMessage(R_.vFileRequired)),
    otherwise: (schema) => schema.notRequired(),
  }),
  url: Yup.string().when('resourceMode', {
    is: 'url',
    then: (schema) => schema.required(intl.formatMessage(R_.vUrlRequired)).url(intl.formatMessage(R_.vUrlInvalid)),
    otherwise: (schema) => schema.notRequired(),
  }),
})

const INIT_FORM = {
  name: '',
  description: '',
  file: null as File | null,
  thumbnail: null as File | null,
  url: '',
  resourceMode: 'file' as 'file' | 'url',
  resourceType: 'document',
  documentTagId: '',
  categoryId: '',
  comments: '',
  isActive: true,
  order: 0,
}

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  // Carpeta actualmente seleccionada en la página (se usa como default)
  defaultCategoryId?: string | null
}

export const ModalAddResource: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  defaultCategoryId = null,
}) => {
  const intl = useIntl()
  const [uploadFile, { data, error, loading }] = useUploadFile<ICreateResourceCenterResponse>()
  const { onOpen: onOpenManageTags, isOpen: isOpenManageTags, onClose: onCloseManageTags } = useDisclourse()
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  const {
    data: dataTags,
    loading: loadingTags,
    error: errorTags,
  } = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-first',
  })

  // El modal consulta el árbol directamente (cache-and-network) para que las
  // carpetas recién creadas aparezcan siempre, sin depender del estado de la página
  const { data: dataCategories, loading: loadingCategories } =
    useQuery<IResourceCategoryTreeResponse>(GET_RESOURCE_CATEGORY_TREE, {
      fetchPolicy: 'cache-and-network',
    })

  const formik = useFormik({
    initialValues: { ...INIT_FORM, categoryId: defaultCategoryId || '' },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      if (v.resourceMode === 'file' && !v.file) {
        toast.error(intl.formatMessage(R_.warnSelectFile))
        return
      }

      if (v.resourceMode === 'url' && !v.url) {
        toast.error(intl.formatMessage(R_.warnEnterUrl))
        return
      }

      setUploadProgress(0)

      const formdata = new FormData()

      // Mapa multipart dinámico: archivo principal y/o miniatura
      const map: Record<string, string[]> = {}
      let partIndex = 0
      if (v.resourceMode === 'file' && v.file) {
        formdata.append(String(partIndex), v.file, v.file.name)
        map[String(partIndex)] = ['variables.resourceData.file']
        partIndex++
      }
      if (v.thumbnail) {
        formdata.append(String(partIndex), v.thumbnail, v.thumbnail.name)
        map[String(partIndex)] = ['variables.resourceData.thumbnail']
        partIndex++
      }

      const obj = {
        query: CREATE_RESOURCE_CENTER_STR,
        variables: {
          resourceData: {
            name: v.name,
            description: v.description || undefined,
            file: v.resourceMode === 'file' ? null : undefined,
            thumbnail: v.thumbnail ? null : undefined,
            url: v.resourceMode === 'url' ? v.url : undefined,
            resourceType: v.resourceType,
            documentTagId: v.documentTagId ? parseId(v.documentTagId) : undefined,
            categoryId: v.categoryId ? parseId(v.categoryId) : undefined,
            comments: v.comments || undefined,
            isActive: v.isActive,
            isCampaignMedia: false, // Mark as Resource Center (not campaign media)
            order: v.order,
          },
        },
      }

      formdata.append('map', JSON.stringify(map))
      formdata.append('operations', JSON.stringify(obj))

      try {
        setUploadProgress(50)
        await uploadFile(formdata)
        setUploadProgress(100)
      } catch (err) {
        console.error('Error uploading resource:', err)
        toast.error(intl.formatMessage(R_.errUpload))
      }
    },
  })

  // Generate preview for images
  useEffect(() => {
    if (formik.values.file && formik.values.file.type.startsWith('image/')) {
      const url = URL.createObjectURL(formik.values.file)
      setPreview(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setPreview(null)
    }
  }, [formik.values.file])

  // Handle paste
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!isOpen) return
      if (!e.clipboardData) return

      const files = Array.from(e.clipboardData.files || [])
      if (files.length > 0 && files[0] instanceof File) {
        e.preventDefault()
        setIsDragging(true)
        formik.setFieldValue('file', files[0])
        if (!formik.values.name) {
          formik.setFieldValue('name', files[0].name.replace(/\.[^/.]+$/, ''))
        }
        toast.info(intl.formatMessage(R_.toastFilePasted))
        setTimeout(() => setIsDragging(false), 1000)
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [isOpen, formik])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.toString()}))
  }, [error])

  useEffect(() => {
    if (errorTags) toast.error(intl.formatMessage(BACKEND_ERROR, {message: errorTags.message}))
  }, [errorTags])

  useEffect(() => {
    if (!data) return
    if (data.createResourceCenter.success) {
      onClose()
      refetch()
      toast.success(intl.formatMessage(R_.toastResourceCreated))
      formik.resetForm()
    } else if (
      data.createResourceCenter.errors &&
      data.createResourceCenter.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.createResourceCenter.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    formik.resetForm()
    setPreview(null)
    setUploadProgress(0)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-plus-circle" /> {intl.formatMessage(R_.addNewResource)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="mb-3">
            <span className="text-muted">{intl.formatMessage(R_.uploading)}</span>
            <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
          </div>
        )}

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Resource Mode Selector */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.resourceType)}</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="mode-file"
                    name="resourceMode"
                    label={intl.formatMessage(R_.uploadFile)}
                    checked={formik.values.resourceMode === 'file'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'file')
                      formik.setFieldValue('url', '')
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="mode-url"
                    name="resourceMode"
                    label={intl.formatMessage(R_.externalUrl)}
                    checked={formik.values.resourceMode === 'url'}
                    onChange={() => {
                      formik.setFieldValue('resourceMode', 'url')
                      formik.setFieldValue('file', null)
                      setPreview(null)
                    }}
                  />
                </div>
              </Form.Group>
            </Col>

            {/* File Upload */}
            {formik.values.resourceMode === 'file' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    {intl.formatMessage(R_.file)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                  </Form.Label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragging(true)
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDragging(false)
                      const files = Array.from(e.dataTransfer.files || [])
                      if (files.length > 0 && files[0] instanceof File) {
                        formik.setFieldValue('file', files[0])
                        if (!formik.values.name) {
                          formik.setFieldValue('name', files[0].name.replace(/\.[^/.]+$/, ''))
                        }
                      }
                    }}
                    className={`border rounded p-4 text-center ${isDragging ? 'bg-light border-primary' : 'border-secondary'
                      }`}
                    style={{ cursor: 'pointer', borderStyle: 'dashed' }}
                  >
                    {isDragging ? (
                      <span className="text-primary fw-bold">{intl.formatMessage(R_.dropFileHere)}</span>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload fs-1 text-muted d-block mb-2" />
                        <span className="text-muted">
                          {intl.formatMessage(R_.dragOrClick)}
                        </span>
                      </>
                    )}
                  </div>

                  <Form.Control
                    name="file"
                    isInvalid={Boolean(formik.errors.file && formik.touched.file)}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const file = (e as ChangeEvent<HTMLInputElement>)?.target?.files?.[0]
                      if (file) {
                        formik.setFieldValue('file', file)
                        if (!formik.values.name) {
                          formik.setFieldValue('name', file.name.replace(/\.[^/.]+$/, ''))
                        }
                      }
                    }}
                    type="file"
                    className="mt-2"
                  />

                  {formik.values.file && (
                    <div className="mt-3 p-3 border rounded bg-light">
                      <div className="d-flex align-items-center gap-3">
                        {preview ? (
                          <img
                            src={preview}
                            alt={intl.formatMessage(R_.previewAlt)}
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              backgroundColor: '#e9ecef',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 4,
                            }}
                          >
                            <i className="bi bi-file-earmark fs-1 text-muted" />
                          </div>
                        )}
                        <div className="flex-fill">
                          <div className="fw-bold">{formik.values.file.name}</div>
                          <small className="text-muted">
                            {(formik.values.file.size / 1024).toFixed(2)}
{KB_UNIT}
                          </small>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            formik.setFieldValue('file', null)
                            setPreview(null)
                          }}
                        >
                          <i className="bi bi-x-lg" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {formik.touched.file && formik.errors.file && (
                    <Form.Text className="text-danger">{formik.errors.file}</Form.Text>
                  )}
                </Form.Group>
              </Col>
            )}

            {/* URL Input */}
            {formik.values.resourceMode === 'url' && (
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    {intl.formatMessage(R_.url)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                  </Form.Label>
                  <Form.Control
                    name="url"
                    type="url"
                    placeholder={intl.formatMessage(R_.urlPlaceholder)}
                    value={formik.values.url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={Boolean(formik.errors.url && formik.touched.url)}
                  />
                  {formik.touched.url && formik.errors.url && (
                    <Form.Text className="text-danger">{formik.errors.url}</Form.Text>
                  )}
                  <Form.Text className="text-muted">
                    {intl.formatMessage(R_.urlSupported)}
                  </Form.Text>
                </Form.Group>
              </Col>
            )}

            {/* Name */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(R_.name)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Form.Control
                  isInvalid={Boolean(formik.errors.name && formik.touched.name)}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  name="name"
                  placeholder={intl.formatMessage(R_.namePlaceholder)}
                />
                {formik.errors.name && formik.touched.name && (
                  <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Resource Type */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  {intl.formatMessage(R_.resourceType)}
<span className="text-danger ms-1">{REQUIRED_MARK}</span>
                </Form.Label>
                <Select
                  name="resourceType"
                  onBlur={formik.handleBlur}
                  value={resourceTypeOptions(intl).find((opt) => opt.value === formik.values.resourceType)}
                  onChange={(option) => {
                    formik.setFieldValue('resourceType', option?.value || 'document')
                  }}
                  options={resourceTypeOptions(intl)}
                />
                {formik.errors.resourceType && formik.touched.resourceType && (
                  <Form.Text className="text-danger">{formik.errors.resourceType}</Form.Text>
                )}
              </Form.Group>
            </Col>

            {/* Description */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.descriptionField)}</Form.Label>
                <Form.Control
                  name="description"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  as="textarea"
                  rows={2}
                  placeholder={intl.formatMessage(R_.descriptionPlaceholder)}
                />
              </Form.Group>
            </Col>

            {/* Thumbnail (opcional) */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.thumbnail)}</Form.Label>
                <div className="d-flex align-items-center gap-3">
                  {formik.values.thumbnail && (
                    <div style={{ position: 'relative' }}>
                      <img
                        src={URL.createObjectURL(formik.values.thumbnail)}
                        alt={intl.formatMessage(R_.thumbnailAlt)}
                        style={{ width: 90, height: 60, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="position-absolute top-0 end-0 p-0 px-1"
                        onClick={() => formik.setFieldValue('thumbnail', null)}
                      >
                        <i className="bi bi-x" />
                      </Button>
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = (e as ChangeEvent<HTMLInputElement>)?.target?.files?.[0]
                      if (file) formik.setFieldValue('thumbnail', file)
                    }}
                  />
                </div>
                <Form.Text className="text-muted">
                  {intl.formatMessage(R_.thumbnailHint)}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Tag */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <div className="d-flex align-items-center justify-content-between">
                  <Form.Label className="fw-bold">{intl.formatMessage(R_.tag)}</Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mb-2 text-decoration-none"
                    onClick={onOpenManageTags}
                    title={intl.formatMessage(R_.createNewTag)}
                  >
                    <i className="bi bi-plus-circle me-1" />
                    {intl.formatMessage(R_.newTag)}
                  </Button>
                </div>
                <Select
                  isClearable
                  isLoading={loadingTags}
                  value={
                    formik.values.documentTagId
                      ? dataTags?.allAttachmentsTags.edges
                          .map(({ node }) => ({ label: node.tagName, value: node.id }))
                          .find((opt) => opt.value === formik.values.documentTagId) || null
                      : null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('documentTagId', option?.value || '')
                  }}
                  options={
                    dataTags?.allAttachmentsTags.edges.map(({ node }) => ({
                      label: node.tagName,
                      value: node.id,
                    })) || []
                  }
                  placeholder={intl.formatMessage(R_.selectTag)}
                />
              </Form.Group>
            </Col>

            {/* Folder */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.folder)}</Form.Label>
                <Select
                  isClearable
                  isLoading={loadingCategories}
                  value={
                    flattenCategoryTree(dataCategories?.resourceCategoryTree || []).find(
                      (opt) => opt.value === formik.values.categoryId
                    ) || null
                  }
                  onChange={(option) => {
                    formik.setFieldValue('categoryId', option?.value || '')
                  }}
                  options={flattenCategoryTree(dataCategories?.resourceCategoryTree || [])}
                  placeholder={intl.formatMessage(R_.noFolderUncategorized)}
                />
              </Form.Group>
            </Col>

            {/* Order */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.displayOrder)}</Form.Label>
                <Form.Control
                  type="number"
                  name="order"
                  value={formik.values.order}
                  onChange={formik.handleChange}
                  placeholder="0"
                  min="0"
                />
                <Form.Text className="text-muted">
                {intl.formatMessage(R_.orderHintPriority)}
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Comments */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{intl.formatMessage(R_.comments)}</Form.Label>
                <Form.Control
                  name="comments"
                  onChange={formik.handleChange}
                  value={formik.values.comments}
                  as="textarea"
                  rows={3}
                  placeholder={intl.formatMessage(R_.commentsPlaceholder)}
                />
              </Form.Group>
            </Col>

            {/* Is Active */}
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label={intl.formatMessage(R_.activeVisible)}
                  checked={formik.values.isActive}
                  onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
        {intl.formatMessage(R_.cancel)}
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={loading || loadingTags}
          onClick={() => formik.handleSubmit()}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.uploading)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" />
              {intl.formatMessage(R_.createResource)}
            </>
          )}
        </Button>
      </Modal.Footer>

      {isOpenManageTags && (
        <ModalManageTags
          isOpen={isOpenManageTags}
          onClose={onCloseManageTags}
          onTagCreated={(tag) => formik.setFieldValue('documentTagId', tag.id)}
        />
      )}
    </Modal>
  )
}
